/* Reusable contextual help — "help must appear exactly where it's needed".
   A quiet trigger that opens the relevant official guide in a side sheet,
   scrolled to the exact section, with a link to the full article. Cached. */
import { ArrowUpRight, HelpCircle, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ArticleBlocks } from "@/components/resources/ArticleBlocks";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { api } from "@/lib/api";

const cache = new Map();

export const LearnMore = ({ slug, section, className = "" }) => {
  const { t, i18n } = useTranslation();
  const cacheKey = `${slug}:${i18n.resolvedLanguage}`;
  const [open, setOpen] = useState(false);
  const [article, setArticle] = useState(() => cache.get(cacheKey) || null);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const cached = cache.get(cacheKey);
    if (cached) {
      setArticle(cached);
      return;
    }
    setArticle(null);
    api
      .get(`/resources/articles/${slug}`, { params: { locale: i18n.resolvedLanguage } })
      .then(({ data }) => {
        cache.set(cacheKey, data);
        setArticle(data);
      })
      .catch(() => {});
  }, [open, cacheKey, slug, i18n.resolvedLanguage]);

  useEffect(() => {
    if (!open || !article || !section) return;
    const timer = setTimeout(() => {
      bodyRef.current?.querySelector(`#lm-${section}`)?.scrollIntoView({ block: "start" });
    }, 60);
    return () => clearTimeout(timer);
  }, [open, article, section]);

  return (
    <>
      <button
        type="button"
        data-testid={`learn-more-${slug}${section ? `-${section}` : ""}`}
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-1.5 text-xs font-medium text-[#1D4D3B] hover:text-[#163B2D] transition-colors duration-200 ${className}`}
      >
        <HelpCircle className="w-3.5 h-3.5" aria-hidden="true" />
        {t("kb.learnMore")}
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-lg p-0 flex flex-col gap-0" data-testid="learn-more-sheet">
          <SheetHeader className="px-6 pt-6 pb-4 border-b border-black/5 text-left space-y-1.5">
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#1D4D3B]">{t("kb.eyebrow")}</p>
            <SheetTitle className="text-lg font-semibold tracking-tight text-[#0A0A0A] pr-8">
              {article ? article.title : t("kb.learnMore")}
            </SheetTitle>
            {article?.subtitle && (
              <SheetDescription className="text-[13px] text-[#737373] leading-relaxed">{article.subtitle}</SheetDescription>
            )}
          </SheetHeader>
          <div ref={bodyRef} className="flex-1 overflow-y-auto px-6 py-5">
            {!article ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-5 h-5 text-[#0A0A0A]/30 animate-spin" aria-hidden="true" />
              </div>
            ) : (
              article.sections.map((s) => (
                <section
                  key={s.id}
                  id={`lm-${s.id}`}
                  className={`scroll-mt-4 mb-6 ${section === s.id ? "rounded-2xl bg-[#1D4D3B]/[0.04] border border-[#1D4D3B]/15 px-4 py-1 -mx-4" : ""}`}
                >
                  {s.title && <h3 className="text-[15px] font-semibold tracking-tight text-[#0A0A0A] mt-3 mb-1">{s.title}</h3>}
                  <ArticleBlocks blocks={s.blocks} />
                </section>
              ))
            )}
          </div>
          <div className="px-6 py-4 border-t border-black/5 bg-[#FAFAF9]">
            <Link
              to={`/resources/${slug}${section ? `#${section}` : ""}`}
              data-testid="learn-more-open-full"
              onClick={() => setOpen(false)}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0A0A0A] hover:text-[#1D4D3B] transition-colors duration-200"
            >
              {t("kb.openFull")}
              <ArrowUpRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};
